import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { FaTrophy, FaLock } from "react-icons/fa";
import PageHeader from "../components/PageHeader";
import SmackBoard from "../components/SmackBoard";
import RulesPayoutsPanel from "../components/RulesPayoutsPanel";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getSurvivorPool,
  getSurvivorGrid,
  getWeekResults,
  getTeamUsage,
  getSurvivorPayouts,
  getNflCurrentWeek,
  getSvSmackMessages,
  postSvSmackMessage,
  getSvSmackReplies,
  postSvSmackReply,
  reactSvSmack,
  deleteSvSmackMessage,
} from "../api/nflSurvivor";
import { useNflSeason } from "../hooks/useNflSeason";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";
const RANK_COL_W = "190px";

// RulesPayoutsPanel expects sections of { heading, lines[] }, not bare strings.
const SURVIVOR_RULES = [
  {
    heading: "Picking",
    lines: [
      "Pick one winner each week.",
      "A team can only be picked once during the season.",
      "Picks lock at that game's kickoff — you can still pick a later game the same week.",
      "Teams on a bye can't be picked that week.",
    ],
  },
  {
    heading: "Surviving",
    lines: [
      "Your team wins: you advance.",
      "Your team loses OR ties: you are eliminated.",
      "Miss a week and the worst-odds team still available is auto-picked for you.",
    ],
  },
];

/** Colour for a cell based on how that pick turned out. */
function resultStyle(result) {
  switch (result) {
    case "win":
      return { bg: "green.50", border: "green.400" };
    case "loss":
      return { bg: "red.50", border: "red.400" };
    case "tie":
      return { bg: "orange.50", border: "orange.400" };
    case "void":
      return { bg: "gray.100", border: "gray.300" };
    default:
      return { bg: "white", border: "gray.200" };
  }
}

export default function NflSurvivorActiveHome() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { seasonId: hookSeasonId } = useNflSeason();
  const SEASON_ID = location.state?.seasonId ?? hookSeasonId;
  const numericPoolId = Number(poolId);

  const [pool, setPool] = useState(null);
  const [grid, setGrid] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [usage, setUsage] = useState([]);
  const [weekResults, setWeekResults] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!SEASON_ID || !numericPoolId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SEASON_ID, numericPoolId]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [poolData, gridData, payoutData, usageData, weekMeta] = await Promise.all([
        getSurvivorPool(SEASON_ID, numericPoolId),
        getSurvivorGrid(SEASON_ID, numericPoolId),
        getSurvivorPayouts(SEASON_ID, numericPoolId).catch(() => []),
        getTeamUsage(SEASON_ID, numericPoolId).catch(() => ({ teams: [] })),
        getNflCurrentWeek(SEASON_ID).catch(() => null),
      ]);
      setPool(poolData);
      setGrid(gridData);
      setPayouts(payoutData);
      setUsage(usageData.teams || []);

      const wk = weekMeta?.current_week ?? gridData?.weeks?.[0] ?? 1;
      setSelectedWeek(wk);
      const wr = await getWeekResults(SEASON_ID, numericPoolId, wk).catch(() => null);
      setWeekResults(wr);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load pool.");
    } finally {
      setLoading(false);
    }
  }

  async function changeWeek(wk) {
    setSelectedWeek(wk);
    const wr = await getWeekResults(SEASON_ID, numericPoolId, wk).catch(() => null);
    setWeekResults(wr);
  }

  const voided = useMemo(() => new Set(grid?.voided_weeks || []), [grid]);

  // Full club names fit the Results by Week matchup column on desktop; a phone
  // stays on abbreviations.
  const isDesktop = useBreakpointValue({ base: false, md: true }) ?? false;

  // Entries knocked out per team this week. A tie eliminates just like a loss
  // — only a win survives — and a voided week scores every pick "void", so a
  // rolled-back week correctly shows no knockouts. Picks carry no game_id, but
  // a team plays once per week, so the team is enough to attribute them.
  const knockoutsByTeam = useMemo(() => {
    const tally = {};
    (weekResults?.picks || []).forEach((p) => {
      if (p.result !== "loss" && p.result !== "tie") return;
      const id = p.team?.id;
      if (id != null) tally[id] = (tally[id] || 0) + 1;
    });
    return tally;
  }, [weekResults]);
  const standing = grid?.standing;

  if (loading) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading Survivor pool...</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="NFL SURVIVOR"
        onBack={() => navigate("/nfl-survivor")}
        user={user}
        onLogout={() => { logout(); navigate("/login", { replace: true }); }}
      />

      <Container maxW="container.xl" py={8}>
        {error && <Alert status="error" borderRadius="md" mb={6}><AlertIcon />{error}</Alert>}

        <SmackBoard
          poolId={numericPoolId}
          seasonId={SEASON_ID}
          apiFns={{
            getMessages: getSvSmackMessages,
            postMessage: postSvSmackMessage,
            getReplies: getSvSmackReplies,
            postReply: postSvSmackReply,
            react: reactSvSmack,
            deleteMessage: deleteSvSmackMessage,
          }}
        />

        <Flex justify="space-between" align="center" mt={6} mb={4} flexWrap="wrap" gap={3}>
          <HStack spacing={3} flexWrap="wrap">
            <Button size="sm" bg="red.600" color="white" _hover={{ bg: "red.700" }}
                    onClick={() => navigate(`/nfl-survivor/picks/${numericPoolId}`, { state: { seasonId: SEASON_ID } })}>
              Make/Review Picks
            </Button>
            {pool?.owner_user_id === user?.id && (
              <Button size="sm" bg="white" color={TOP_BAR_BLUE} _hover={{ bg: "gray.100" }}
                      onClick={() => navigate(`/nfl-survivor/setup/${numericPoolId}`, { state: { seasonId: SEASON_ID } })}>
                Pool Settings
              </Button>
            )}
          </HStack>
          {standing && (
            <HStack spacing={2}>
              <Badge colorScheme="green" fontSize="sm">{standing.alive} alive</Badge>
              <Badge colorScheme="red" fontSize="sm">{standing.eliminated} out</Badge>
              {pool?.total_pot != null && <Badge colorScheme="yellow" fontSize="sm">Pot ${pool.total_pot}</Badge>}
            </HStack>
          )}
        </Flex>

        {/* ── The grid: entries down the side, weeks across the top ── */}
        <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg"
             boxShadow="lg" p={{ base: 3, md: 6 }}>
          <Flex align="center" justify="space-between" mb={4} flexWrap="wrap" gap={2}>
            <Heading size="md" color={TOP_BAR_BLUE}>
              {pool?.name ? `${pool.name} — Picks` : "Picks"}
            </Heading>
            <Text fontSize="xs" color="gray.500">
              Other players&apos; picks stay hidden until the week kicks off
            </Text>
          </Flex>

          {!grid?.rows?.length ? (
            <Text color="gray.600">No entries yet.</Text>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
                <Thead>
                  <Tr>
                    <Th position="sticky" left={0} zIndex={3} bg="white"
                        w={RANK_COL_W} minW={RANK_COL_W}>Player</Th>
                    {grid.weeks.map((w) => (
                      <Th key={w} textAlign="center" minW="52px"
                          bg={voided.has(w) ? "gray.100" : "white"}>
                        <Tooltip label={voided.has(w) ? `Week ${w} was voided — everyone survived` : `Week ${w}`} hasArrow>
                          <Text fontSize="xs">{w}</Text>
                        </Tooltip>
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {grid.rows.map((row) => {
                    const rowBg = row.is_current_user ? "yellow.50" : "white";
                    return (
                      <Tr key={row.entry_id} opacity={row.is_eliminated ? 0.55 : 1}>
                        <Td position="sticky" left={0} zIndex={2} bg={rowBg}
                            w={RANK_COL_W} minW={RANK_COL_W}
                            fontWeight={row.is_current_user ? "bold" : "normal"}>
                          <HStack spacing={2}>
                            {standing?.winner_entry_ids?.includes(row.entry_id) && standing?.is_complete && (
                              <Box color="yellow.500"><FaTrophy /></Box>
                            )}
                            <Text noOfLines={1} fontSize="sm">{row.display_name}</Text>
                            {row.is_eliminated && (
                              <Badge colorScheme="red" fontSize="0.6rem">wk{row.eliminated_week}</Badge>
                            )}
                          </HStack>
                        </Td>

                        {row.cells.map((cell) => {
                          const isVoid = voided.has(cell.week);
                          if (cell.hidden) {
                            return (
                              <Td key={cell.week} textAlign="center" px={1} bg="gray.50">
                                <Box color="gray.400" display="flex" justifyContent="center"><FaLock size={11} /></Box>
                              </Td>
                            );
                          }
                          if (!cell.pick) {
                            return <Td key={cell.week} textAlign="center" px={1} bg={isVoid ? "gray.100" : undefined} />;
                          }
                          const st = resultStyle(cell.pick.result);
                          const team = cell.pick.team;
                          return (
                            <Td key={cell.week} textAlign="center" px={1} py={1} bg={st.bg}>
                              <Tooltip
                                hasArrow
                                label={`${team?.name || "?"} — ${cell.pick.result}${cell.pick.is_auto_pick ? " (autopick)" : ""}`}
                              >
                                <Box display="inline-flex" alignItems="center" justifyContent="center"
                                     borderWidth="2px" borderColor={st.border} borderRadius="md" p="2px"
                                     borderStyle={cell.pick.is_auto_pick ? "dashed" : "solid"}>
                                  {team?.logo_url ? (
                                    <Image src={team.logo_url} alt={team.short_name} boxSize="24px" objectFit="contain" />
                                  ) : (
                                    <Text fontSize="xs">{team?.short_name}</Text>
                                  )}
                                </Box>
                              </Tooltip>
                            </Td>
                          );
                        })}
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}

          <HStack spacing={4} mt={3} flexWrap="wrap" fontSize="xs" color="gray.600">
            <HStack spacing={1}><Box w="10px" h="10px" bg="green.50" borderWidth="2px" borderColor="green.400" /><Text>Win</Text></HStack>
            <HStack spacing={1}><Box w="10px" h="10px" bg="red.50" borderWidth="2px" borderColor="red.400" /><Text>Loss</Text></HStack>
            <HStack spacing={1}><Box w="10px" h="10px" bg="orange.50" borderWidth="2px" borderColor="orange.400" /><Text>Tie (out)</Text></HStack>
            <HStack spacing={1}><Box w="10px" h="10px" bg="gray.100" borderWidth="2px" borderColor="gray.300" /><Text>Voided week</Text></HStack>
            <HStack spacing={1}><Box w="10px" h="10px" borderWidth="2px" borderStyle="dashed" borderColor="gray.400" /><Text>Autopick</Text></HStack>
          </HStack>
        </Box>

        {/* ── Results by week + team stats ── */}
        <Flex gap={6} mt={6} align="stretch" flexWrap="wrap">
          <Box flex="2" minW={{ base: "100%", lg: "480px" }} bg="white" color="black"
               border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={{ base: 4, md: 6 }}>
            <Flex align="center" justify="space-between" mb={4} gap={3} flexWrap="wrap">
              <Heading size="md" color={TOP_BAR_BLUE}>Results by Week</Heading>
              <HStack>
                {weekResults?.is_voided && <Badge colorScheme="gray">Voided</Badge>}
                <Select size="sm" maxW="130px" value={selectedWeek ?? ""}
                        onChange={(e) => changeWeek(Number(e.target.value))}>
                  {(grid?.weeks || []).map((w) => <option key={w} value={w}>Week {w}</option>)}
                </Select>
              </HStack>
            </Flex>

            {!weekResults ? (
              <Text color="gray.600">No data for this week.</Text>
            ) : (
              <VStack align="stretch" spacing={4}>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Matchup</Th>
                        <Th textAlign="center">Score</Th>
                        <Th textAlign="center">Result</Th>
                        <Th textAlign="center">Knockouts</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {weekResults.games.map((g) => (
                        <Tr key={g.id}>
                          <Td>
                            <HStack spacing={2}>
                              {g.away_team?.logo_url && <Image src={g.away_team.logo_url} boxSize="18px" objectFit="contain" flexShrink={0} />}
                              <Text fontSize="sm" whiteSpace="nowrap">
                                {isDesktop ? g.away_team?.name : g.away_team?.short_name}
                              </Text>
                              <Text fontSize="xs" color="gray.500" flexShrink={0}>@</Text>
                              {g.home_team?.logo_url && <Image src={g.home_team.logo_url} boxSize="18px" objectFit="contain" flexShrink={0} />}
                              <Text fontSize="sm" whiteSpace="nowrap">
                                {isDesktop ? g.home_team?.name : g.home_team?.short_name}
                              </Text>
                            </HStack>
                          </Td>
                          <Td textAlign="center" fontSize="sm">
                            {g.is_final ? `${g.away_score}-${g.home_score}` : "—"}
                          </Td>
                          <Td textAlign="center" fontSize="xs">
                            {!g.is_final ? <Text color="gray.500">Upcoming</Text>
                              : g.is_tie ? <Badge colorScheme="orange">Tie</Badge>
                              : <Text color="green.600">
                                  {(g.winner_team_id === g.home_team?.id ? g.home_team : g.away_team)?.short_name} won
                                </Text>}
                          </Td>
                          <Td textAlign="center" fontSize="sm">
                            {(() => {
                              if (!g.is_final) return <Text color="gray.500">—</Text>;
                              const kos =
                                (knockoutsByTeam[g.home_team?.id] || 0) +
                                (knockoutsByTeam[g.away_team?.id] || 0);
                              return kos > 0
                                ? <Text color="red.600" fontWeight="bold">{kos}</Text>
                                : <Text color="gray.500">0</Text>;
                            })()}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            )}
          </Box>

          <Box flex="1" minW={{ base: "100%", lg: "300px" }} bg="white" color="black"
               border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg" boxShadow="lg" p={{ base: 4, md: 6 }}>
            <Heading size="md" color={TOP_BAR_BLUE} mb={4}>Team Usage</Heading>
            {usage.length === 0 ? (
              <Text color="gray.600" fontSize="sm">No picks made yet.</Text>
            ) : (
              <Box overflowX="auto" maxH="420px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr><Th>Team</Th><Th isNumeric>Used</Th><Th isNumeric>W</Th><Th isNumeric>L</Th></Tr>
                  </Thead>
                  <Tbody>
                    {usage.map((u) => (
                      <Tr key={u.team.id}>
                        <Td>
                          <HStack spacing={2}>
                            {u.team.logo_url && <Image src={u.team.logo_url} boxSize="18px" objectFit="contain" />}
                            <Text fontSize="sm">{u.team.short_name}</Text>
                          </HStack>
                        </Td>
                        <Td isNumeric fontSize="sm">{u.times_picked}</Td>
                        <Td isNumeric fontSize="sm" color="green.600">{u.wins}</Td>
                        <Td isNumeric fontSize="sm" color="red.600">{u.losses + u.ties}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </Flex>
      </Container>

      <RulesPayoutsPanel rules={SURVIVOR_RULES} payouts={payouts} totalPot={pool?.total_pot} />
    </Box>
  );
}
